import React, {useEffect, useRef, useState} from "react";
import bn from "../../utils/bemnames";
import {_t} from "../../utils/translations";
import LoadingBlock from "../LoadingBlock";
import {DateTime} from "luxon";
import {fetchLeagueScheduleData, IMatchScheduleResponse} from "../../api/general";
import {IWidgetProvider, oddsFormats} from "../../utils/types";
import {IMatchScheduleTableEvent} from "../OddsComparisonTable";
import ComponentWrapper from "../MatchCenter/ComponentWrapper";
import {useGlobalState} from "../../contexts/GlobalState";
import {IOutLinks} from "../../api/match-center";
import {
  Accordion,
} from "react-accessible-accordion";
import DatePicker from "react-datepicker";
import FilterBtn from "../FilterBtn";
import {ReactComponent as CalendarSvgIcon} from "../../svg/calendar2.svg";
import MatchScheduleEventsContainer from "./MatchScheduleEventsContainer";

const bem = bn.create("match_schedule");

interface IProps {
  componentName: string;
  allowedProviders: IWidgetProvider[];
  competitionIds: number[];
  isMobile: boolean;
  languageID: number;
  builderComponentId?: number;
  outLinks: IOutLinks;
  oddsFormat: oddsFormats
  containerWidth: number
}

export enum DayTypes {
  LIVE = 'live',
  TODAY = 'today',
  TOMORROW = 'tomorrow'
}

export interface IMatchScheduleEventGroups {
  [DayTypes.LIVE]?: IMatchScheduleTableEvent[],
  [DayTypes.TODAY]?: IMatchScheduleTableEvent[],
  [DayTypes.TOMORROW]?: IMatchScheduleTableEvent[]
}

const MatchSchedule: React.FC<IProps> = (props) => {
  const {
    componentName,
    isMobile,
    competitionIds,
    allowedProviders,
    builderComponentId,
    outLinks,
    oddsFormat,
    containerWidth
  } = props

  const [matchScheduleData, setMatchScheduleData] = useState<IMatchScheduleResponse | null>(null);
  let nextUpdate: React.MutableRefObject<ReturnType<typeof setTimeout> | null> = useRef(null);

  const [loading, setLoading] = useState<Boolean>(true);
  const [loadingTable, setLoadingTable] = useState<Boolean>(true);

  const [requestFailed, setRequestFailed] = useState<Boolean>(false);

  const [dateRange, setDateRange] = useState<[Date, Date]>([
    new Date(new Date().setHours(new Date().getHours() - 2)),
    new Date(new Date().setDate(new Date().getDate() + 7)),
  ]);

  const daysLimit = useRef(0);

  const {globalState} = useGlobalState();

  useEffect(() => {
    const updateMatchSchedule = () => {
      fetchLeagueScheduleData({
        competitionIds: competitionIds,
        widgetID: globalState.widget?.id,
        minStartTime: DateTime.fromJSDate(dateRange[0]).toFormat("yyyy-MM-dd HH:mm:ss"),
        maxStartTime: DateTime.fromJSDate(dateRange[1]).toFormat("yyyy-MM-dd HH:mm:ss"),
        providerIds: allowedProviders.map((provider) => provider.providerId),
        groupByDate: 0,
        withScores: 1,
        withMinutes: 1,
      })
      .then((r: IMatchScheduleResponse) => {
        if (r[0] !== undefined) {
          setMatchScheduleData(r[0]);
          setLoading(false);
          setLoadingTable(false);
          setRequestFailed(false);
          if (nextUpdate.current) {
            clearTimeout(nextUpdate.current)
          }
          nextUpdate.current = setTimeout(() => {
            updateMatchSchedule()
          }, 60000)
        } else {
          if (daysLimit.current < 60) {
            let starting = new Date(dateRange[0]?.setDate(dateRange[0].getDate() + 7));
            let ending = new Date(dateRange[1]?.setDate(dateRange[1].getDate() + 7));
            setDateRange([starting, ending]);
            daysLimit.current = daysLimit.current + 7;
          } else {
            setRequestFailed(true);
            setLoading(false);
            setLoadingTable(false);
          }
        }
      })
      .catch(() => {
        setRequestFailed(true);
        setLoading(false);
        setLoadingTable(false);
      });
    }
    if (dateRange[0] && dateRange[1]) {
      setLoadingTable(true)
      updateMatchSchedule();
    }
  }, [allowedProviders, competitionIds, dateRange, globalState.widget]);

  const getCalendarButtonName = (
    date: string,
    isCustomDate: boolean,
    useRangeDate: boolean,
    startDate: Date,
    endDate: Date | null
  ): string => {
    if (!useRangeDate) {
      return isCustomDate ? DateTime.fromISO(date).toFormat('dd/MM/yyyy') : _t("Select dates");
    } else {
      if (endDate === null) {
        return DateTime.fromJSDate(startDate)?.toFormat('dd/MM/yyyy');
      } else {
        return `${DateTime.fromJSDate(startDate)?.toFormat('dd/MM/yyyy')} - ${DateTime.fromJSDate(endDate)?.toFormat('dd/MM/yyyy')}`;
      }
    }
  }

  return (
    <>
      {loading || loadingTable ? (
        <LoadingBlock height={200} heightMobile={300} isMobile={isMobile}/>
      ) : (
        <>
          <ComponentWrapper
            title={componentName}
            isMobile={isMobile}
            children={
              <div className={bem.b(` max_width ${isMobile ? 'mobile' : ''}`)}>
                {requestFailed ?
                  (
                    <div className="horizontal-margin">{_t("Not available")}</div>
                  ) :
                  (
                    <div className={bem.e('content')}>
                      <Accordion
                        allowZeroExpanded={true}
                        allowMultipleExpanded={true}
                      >
                        {matchScheduleData !== null && (
                          <MatchScheduleEventsContainer
                            matchScheduleData={matchScheduleData}
                            builderComponentId={builderComponentId}
                            isMobile={isMobile}
                            outLinks={outLinks}
                            oddsFormat={oddsFormat}
                            containerWidth={containerWidth}
                          />
                        )}
                      </Accordion>
                    </div>
                  )
                }
              </div>
            }
            headerContent={
              <div className={`${bem.b(isMobile ? 'mobile' : '')} ${bem.e('header')}`}>
                <div className={bem.e('header__title')}>
                  {!isMobile && (<h2>{componentName}</h2>)}
                </div>
                <div className={bem.e('header__controls')}>
                  <DatePicker
                    selected={dateRange[0]}
                    onChange={(updatedDate: [Date, Date]) => {
                      updatedDate && setDateRange(updatedDate);
                    }}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    selectsRange={true}
                    shouldCloseOnSelect={false}
                    className="flex_row center"
                    customInput={
                      <FilterBtn filterKey={dateRange[0]} activeKey={dateRange[0]} clickOnActive>
                        <span>
                          {getCalendarButtonName(dateRange[0].toDateString(), true, true, dateRange[0], dateRange[1])}
                        </span>
                        <span className="filter_calendar_svg">
                          <CalendarSvgIcon/>{" "}
                        </span>
                      </FilterBtn>
                    }
                  />
                </div>
              </div>
            }
          />
        </>
      )}
    </>
  );
};

export default MatchSchedule;
